import React from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  type PressableProps,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { tokens } from '../tokens';
import { Colors } from '../../../themes/colors';

export interface LinkProps extends Omit<PressableProps, 'style' | 'children'> {
  /**
   * Link text content
   */
  children: string;

  /**
   * Link size variant
   * - small: 12px (label-small)
   * - medium: 14px (label-medium/body-small)
   * - large: 16px (body-medium)
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Whether to emphasize (medium weight) the link
   * @default false
   */
  emphasised?: boolean;

  /**
   * Link color variant
   * - primary: Orange brand color
   * - neutral: Dark neutral color
   * - inherit: Inherit from parent
   * @default 'primary'
   */
  color?: 'primary' | 'neutral' | 'inherit';

  /**
   * Whether to show underline
   * @default true
   */
  underline?: boolean;

  /**
   * Icon to show on the left side
   */
  leftIcon?: React.ReactNode;

  /**
   * Icon to show on the right side
   */
  rightIcon?: React.ReactNode;

  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;

  /**
   * Custom container style
   */
  style?: ViewStyle;

  /**
   * Custom text style
   */
  textStyle?: TextStyle;

  /**
   * Accessible label for screen readers
   */
  accessibilityLabel?: string;

  /**
   * URL to open (for web links)
   * When provided on web, renders as <a> tag
   */
  href?: string;

  /**
   * Whether to open in new tab (web only)
   * @default false
   */
  external?: boolean;

  /**
   * Whether to allow text to wrap to multiple lines
   * @default false
   */
  wrap?: boolean;
}

/**
 * Link Component
 *
 * A flexible link/anchor component for navigation and actions.
 * Supports multiple sizes, colors, and optional icons.
 * Extracted from Figma Crop Circle System.
 *
 * @example
 * ```tsx
 * // Primary link
 * <Link onPress={handlePress}>
 *   Learn more
 * </Link>
 *
 * // Large emphasized link
 * <Link size="large" emphasised>
 *   View details
 * </Link>
 *
 * // Link with icon
 * <Link rightIcon={<ArrowRightIcon size={16} />}>
 *   Continue
 * </Link>
 *
 * // External web link
 * <Link href="https://example.com" external>
 *   Visit website
 * </Link>
 * ```
 *
 * Figma: https://www.figma.com/design/knAFKgkS8wkIQ47XbZAe4S/Crop-Circle-System?node-id=603-8791
 */
export const Link: React.FC<LinkProps> = ({
  children,
  size = 'medium',
  emphasised = false,
  color = 'primary',
  underline = true,
  leftIcon,
  rightIcon,
  disabled = false,
  style,
  textStyle,
  accessibilityLabel,
  href,
  external = false,
  wrap = false,
  ...pressableProps
}) => {
  // Get typography style based on size and emphasis
  const typographyStyle = getTypographyStyle(size, emphasised);

  // Get link color
  const linkColor = getLinkColor(color, disabled);

  const content = wrap ? (
    <Text
      style={[
        typographyStyle,
        {
          color: linkColor,
          textDecorationLine: underline ? 'underline' : 'none',
        },
        disabled && styles.disabledText,
        textStyle,
      ]}
    >
      {leftIcon && (
        <View style={styles.iconInline}>{leftIcon}</View>
      )}
      {leftIcon && ' '}
      {children}
      {rightIcon && ' '}
      {rightIcon && (
        <View style={styles.iconInline}>{rightIcon}</View>
      )}
    </Text>
  ) : (
    <View style={styles.content}>
      {leftIcon && <View style={styles.iconLeft}>{leftIcon}</View>}
      <Text
        style={[
          typographyStyle,
          {
            color: linkColor,
            textDecorationLine: underline ? 'underline' : 'none',
          },
          disabled && styles.disabledText,
          textStyle,
        ]}
        numberOfLines={1}
      >
        {children}
      </Text>
      {rightIcon && <View style={styles.iconRight}>{rightIcon}</View>}
    </View>
  );

  return (
    <Pressable
      {...pressableProps}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel || children}
      accessibilityRole="link"
      accessibilityState={{ disabled }}
      // @ts-ignore - href is web-only
      href={href}
      // @ts-ignore - target is web-only
      target={external ? '_blank' : undefined}
      // @ts-ignore - rel is web-only
      rel={external ? 'noopener noreferrer' : undefined}
      style={({ pressed }) => [
        styles.container,
        pressed && !disabled && styles.pressed,
        disabled && styles.disabled,
        style,
      ]}
    >
      {content}
    </Pressable>
  );
};

/**
 * Get typography style based on size and emphasis
 */
function getTypographyStyle(
  size: 'small' | 'medium' | 'large',
  emphasised: boolean
): TextStyle {
  switch (size) {
    case 'large':
      return emphasised
        ? tokens.typography.link.largeEmphasised
        : tokens.typography.link.large;

    case 'small':
      return emphasised
        ? tokens.typography.link.smallEmphasised
        : tokens.typography.link.small;

    case 'medium':
    default:
      return emphasised
        ? tokens.typography.link.mediumEmphasised
        : tokens.typography.link.medium;
  }
}

/**
 * Get link color based on variant and disabled state
 */
function getLinkColor(
  color: 'primary' | 'neutral' | 'inherit',
  disabled: boolean
): string {
  if (disabled) {
    return Colors.role.textDisabled; // #B4B4B4 in light
  }

  switch (color) {
    case 'primary':
      return tokens.colors.orange[500]; // Brand orange

    case 'neutral':
      return Colors.role.textStrong;

    case 'inherit':
    default:
      return 'inherit';
  }
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start', // Don't stretch to full width
  },

  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs, // 4px between icon and text
  },

  iconInline: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  iconLeft: {
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },

  iconRight: {
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },

  pressed: {
    opacity: 0.7,
  },

  disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  disabledText: {
    textDecorationLine: 'none', // Remove underline when disabled
  },
});

Link.displayName = 'Link';
